import { Stack, Box, Divider, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from '@chr/online-shared-ui-localizations';
import { ServiceStatusRdn } from '@chr/online-shared-ui-constants';

import {
  getRollupBookingStatusLabelOrDefault,
  IsoToDate,
  IsoToDateAtTime, TranslateType
} from '@/common/utilities/display-utilities';

import { LocationInfo, VisibilityBooking } from '@/common/apis/dtos';
import { InformationDisplay } from '@/common/components/information-display/InformationDisplay';
import { Location } from '@/common/components/location/Location';
import { HazMatSvgIcon, TempSvgIcon } from '@/common/components/icons/Icons';
import { getMaxActivityDate, getTransportModeFromDesc } from '@/common/utilities/booking';
import EtaIconContainer from '@/common/components/eta-icon/EtaIconContainer';
import BookingConditionBadge from './BookingCondition';
import { TransportMode } from '@/common/constants/booking-types';
import ControlledTooltip from '@/common/components/controlled-tooltip/ControlledTooltip';

const displayPickupDate = (stop?: LocationInfo) => {
  const locationDate = stop?.actualDepartureDateTime || stop?.requestedDate || stop?.readyByDate;
  return locationDate ? IsoToDate(locationDate) : '-';
};

const displayDeliveryDate = (stop?: LocationInfo) => {
  const locationDate = stop?.actualDepartureDateTime || stop?.requestedDate;
  return locationDate ? IsoToDate(locationDate) : '-';
};

const displayPickupFromDateLabel = (stop: LocationInfo | undefined, translate: TranslateType): string => {
  if (stop?.actualDepartureDateTime) {
    return translate(`Actual Date`);
  }

  if (stop?.requestedDate) {
    return translate(`Requested Date`);
  }

  if (stop?.readyByDate) {
    return translate(`Ready By Date`);
  }

  return translate(`Requested Date`);
};

const displayDeliverToDateLabel = (stop: LocationInfo | undefined, translate: TranslateType): string => {
  if (stop?.actualDepartureDateTime) {
    return translate(`Actual Date`);
  }

  if (stop?.requestedDate) {
    return translate(`Requested Date`);
  }

  return translate(`Requested Date`);
};

export interface BookingSummaryProps {
  booking: VisibilityBooking;
}

const BookingSummary = ({ booking }: BookingSummaryProps) => {
  const translate = useTranslation();
  const { breakpoints } = useTheme();
  const isMobileScreen = useMediaQuery(breakpoints.down('md'));

  // Bookings have a single service.
  const service = booking.services?.[0] ?? {};

  const bookingStatusId = booking.status?.id;
  const serviceOffering = service.serviceOffering ?? {};

  const modeDescription = serviceOffering.modeDescription;
  const transportMode = getTransportModeFromDesc(modeDescription);

  const conditionRdn = booking.conditionRdn;
  const accountName = booking.customerInfo?.customer?.name;
  const shipment = booking.shipment;

  const pickupFrom = { ...(service.pickupFrom ?? {}) };
  const deliverTo = { ...(service.deliverTo ?? {}) };

  const shipmentStops =
    (booking.shipment?.stops ?? [])
      .filter(x => x.stopNumber != undefined)
      .sort((a, b) => (a.stopNumber ?? 0) - (b.stopNumber ?? 0));

  const shipmentCarrier = booking.shipment?.primaryCarrierSummary?.carrierName ?? '';

  const firstShipmentStop = shipmentStops[0] ?? {};
  const lastShipmentStop = shipmentStops.length > 1 ? shipmentStops?.[shipmentStops.length - 1] : {};

  // Note, if there isn't an actual date from the shipment, this will make sure we overwrite spurious dates in booking
  pickupFrom.actualDepartureDateTime = getMaxActivityDate(firstShipmentStop.activities ?? [], 'departureDateTime');
  deliverTo.actualDepartureDateTime = getMaxActivityDate(lastShipmentStop.activities ?? [], 'departureDateTime');

  const containsHazmatItems = booking.isHazmat;
  const containsTempControlledItems = booking.isTemperatureControlled;
  const showBookingCondition = bookingStatusId !== ServiceStatusRdn.DELIVERED;

  return (
    <Grid container>
      <Grid item sm={3} md={3} xs={12} p={2}>
        <Stack gap={2}>
          <ControlledTooltip title={accountName}>
            <Typography component="p" variant="h3" fontSize={16} noWrap data-testid="account-name">
              {accountName}
            </Typography>
          </ControlledTooltip>

          <InformationDisplay label={translate('CHR Booking Number')} text={booking.customerBookingNumber} />

          <InformationDisplay
            label={translate('Created On')}
            text={IsoToDateAtTime(booking.createDateTime, translate, true)}
          />
        </Stack>
      </Grid>

      <Grid container item sm={3} md={3} xs={12} p={2} sx={{ backgroundColor: 'grey.50' }}>
        <Stack justifyContent="space-between" gap={2}>
          {pickupFrom?.location && (
            <InformationDisplay
              label={translate('Pickup From')}
              component={<Location {...pickupFrom.location} />}
            />
          )}

          <InformationDisplay
            label={displayPickupFromDateLabel(pickupFrom, translate)}
            text={displayPickupDate(pickupFrom)}
            customTestId={`${displayPickupFromDateLabel(pickupFrom, translate)} pickup`}
            labelIcon={(<EtaIconContainer displayEta={shipment?.displayEta} riskLevel={shipment?.riskLevel} />)}
          />
        </Stack>
      </Grid>

      <Grid item sm="auto" md="auto" xs={12}>
        <Divider orientation={isMobileScreen ? 'horizontal' : 'vertical'} sx={{ borderColor: 'grey.200' }} />
      </Grid>

      <Grid container item sm={3} md={3} xs={12} p={2} sx={{ backgroundColor: 'grey.50' }}>
        <Stack justifyContent="space-between" gap={2}>
          {deliverTo?.location && (
            <InformationDisplay
              label={translate('Deliver To')}
              component={<Location {...deliverTo.location} />}
            />
          )}

          <InformationDisplay
            label={displayPickupFromDateLabel(deliverTo, translate)}
            text={displayDeliveryDate(deliverTo)}
            customTestId={`${displayDeliverToDateLabel(deliverTo, translate)} deliver`}
          />
        </Stack>
      </Grid>

      <Grid item sm="auto" md={true} xs={12} p={2}>
        <Stack gap={2} height="100%" justifyContent="space-between">
          <Stack direction={'row'} spacing={1}>
            <InformationDisplay
              label={translate('Status')}
              text={getRollupBookingStatusLabelOrDefault(translate, bookingStatusId)}
            />

            {showBookingCondition && (
              <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <BookingConditionBadge conditionRDN={conditionRdn} />
              </Box>
            )}
          </Stack>

          <InformationDisplay
            label={translate('Shipping Method')}
            component={
              <Box display="flex" alignItems="center">
                <Typography variant='subtitle2'>{translate(modeDescription ?? '-')}</Typography>
                <Box display="flex" gap="4px" paddingLeft={0.75}>
                  {containsHazmatItems && <HazMatSvgIcon />}
                  {containsTempControlledItems && <TempSvgIcon />}
                </Box>
              </Box>
            }
          />
          {(transportMode !== TransportMode.AIR) &&
            <InformationDisplay
              label={translate('Booking Type')}
              text={translate(serviceOffering.capacity ?? '-')}
            />
          }

          <InformationDisplay
            label={translate('Carrier')}
            component={
              <>
                {shipmentCarrier.length > 0 ?
                  <ControlledTooltip title={shipmentCarrier}>
                    <Typography component="div" variant='subtitle2' data-testid={`carrier-name`} noWrap>
                      {shipmentCarrier}
                    </Typography>
                  </ControlledTooltip> :
                  '-'
                }
              </>
            }
          />
        </Stack>
      </Grid>
    </Grid>
  );
};

export default BookingSummary;
